import React, { useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { AlertCircle, Plus, Trash2, ChevronDown, ChevronUp } from 'react-feather';
import { FormTitle, FormSubtitle, FormGroup, FormRow, Label, StyledInput, StyledSelect, CheckboxLabel, ErrorText, SecondaryButton } from '../styles';
import { PhoneInput } from '../../../../common/PhoneInput';

const Step3BeneficialOwner = () => {
    const { register, watch, control, formState: { errors } } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "beneficiaries"
    });

    const [expandedIndex, setExpandedIndex] = useState(0);
    const beneficiaries = watch('beneficiaries');

    return (
        <div>
            <FormTitle>Beneficial Owners</FormTitle>
            <FormSubtitle>All individuals with 25%+ ownership (direct or indirect) must be identified. (Max 4)</FormSubtitle>

            {fields.map((field, index) => {
                const idType = beneficiaries?.[index]?.repIdType;
                const isPEP = beneficiaries?.[index]?.isPEP;
                const fieldErrors = errors?.beneficiaries?.[index];
                const isExpanded = expandedIndex === index;
                const hasErrors = fieldErrors && Object.keys(fieldErrors).length > 0;

                return (
                    <div key={field.id} style={{ 
                        border: '1px solid rgba(255,255,255,0.08)', 
                        borderRadius: '12px', 
                        marginBottom: '16px',
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        overflow: 'hidden'
                    }}>
                        {/* Accordion Header */}
                        <div 
                            onClick={() => setExpandedIndex(isExpanded ? null : index)}
                            style={{ 
                                padding: '16px 20px', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                cursor: 'pointer',
                                backgroundColor: isExpanded ? 'rgba(255,255,255,0.04)' : 'transparent',
                                borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.08)' : 'none'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: hasErrors ? '#ef4444' : 'inherit' }}>
                                    Owner #{index + 1}: {beneficiaries?.[index]?.repName || 'New Owner'}
                                </h4>
                                {hasErrors && <AlertCircle size={14} style={{ color: '#ef4444' }} />}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {fields.length > 1 && (
                                    <Trash2 
                                        size={16} 
                                        style={{ color: '#ef4444', opacity: 0.7 }} 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            remove(index);
                                            if (expandedIndex === index) setExpandedIndex(null);
                                        }} 
                                    />
                                )}
                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        </div>

                        {/* Accordion Content */}
                        {isExpanded && (
                            <div style={{ padding: '24px' }}>
                                <FormRow>
                                    <FormGroup>
                                        <Label>Role</Label>
                                        <StyledSelect {...register(`beneficiaries.${index}.roleType`)} $error={!!fieldErrors?.roleType}>
                                            <option value="">Select Role</option>
                                            <option value="ownership">Beneficial Owner (25%+ Ownership)</option>
                                            <option value="control">Control Person (CEO, Director, etc.)</option>
                                        </StyledSelect>
                                        {fieldErrors?.roleType && <ErrorText>{fieldErrors.roleType.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Full Legal Name</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`beneficiaries.${index}.repName`)} 
                                            placeholder="John Doe" 
                                            $error={!!fieldErrors?.repName} 
                                        />
                                        {fieldErrors?.repName && <ErrorText>{fieldErrors.repName.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Title / Position</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`beneficiaries.${index}.repTitle`)} 
                                            placeholder="CEO / Founder" 
                                            $error={!!fieldErrors?.repTitle} 
                                        />
                                        {fieldErrors?.repTitle && <ErrorText>{fieldErrors.repTitle.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Ownership %</Label>
                                        <StyledInput
                                            type="number"
                                            {...register(`beneficiaries.${index}.ownershipPercentage`, { valueAsNumber: true })}
                                            placeholder="e.g. 50"
                                            min="0"
                                            max="100"
                                            $error={!!fieldErrors?.ownershipPercentage}
                                        />
                                        {fieldErrors?.ownershipPercentage && <ErrorText>{fieldErrors.ownershipPercentage.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Date of Birth</Label>
                                        <StyledInput type="date" {...register(`beneficiaries.${index}.repDob`)} $error={!!fieldErrors?.repDob} />
                                        {fieldErrors?.repDob && <ErrorText>{fieldErrors.repDob.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Email Address</Label>
                                        <StyledInput 
                                            type="email" 
                                            {...register(`beneficiaries.${index}.repEmail`)} 
                                            placeholder="john@example.com" 
                                            $error={!!fieldErrors?.repEmail} 
                                        />
                                        {fieldErrors?.repEmail && <ErrorText>{fieldErrors.repEmail.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Phone Number</Label>
                                        <PhoneInput
                                            dialCodeName={`beneficiaries.${index}.repPhoneDialCode`}
                                            numberName={`beneficiaries.${index}.repPhone`}
                                            register={register}
                                            errors={errors}
                                            index={index}
                                            arrayName="beneficiaries"
                                        />
                                        {fieldErrors?.repPhone && <ErrorText>{fieldErrors.repPhone.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Government ID Type</Label>
                                        <StyledSelect {...register(`beneficiaries.${index}.repIdType`)} $error={!!fieldErrors?.repIdType}>
                                            <option value="">Select ID type</option>
                                            <option value="ssn">SSN / ITIN (U.S.)</option>
                                            <option value="passport">Passport</option>
                                        </StyledSelect>
                                        {fieldErrors?.repIdType && <ErrorText>{fieldErrors.repIdType.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>
                                            {idType === 'passport' ? 'Passport Number' : 'SSN / ITIN'}
                                        </Label>
                                        <StyledInput
                                            type="text"
                                            {...register(`beneficiaries.${index}.repSSN`)}
                                            placeholder={idType === 'passport' ? 'A12345678' : 'XXX-XX-XXXX'}
                                            $error={!!fieldErrors?.repSSN}
                                        />
                                        {fieldErrors?.repSSN && <ErrorText>{fieldErrors.repSSN.message}</ErrorText>}
                                    </FormGroup>

                                    {idType === 'passport' && (
                                        <FormGroup>
                                            <Label>Issuing Country</Label>
                                            <StyledInput
                                                type="text"
                                                {...register(`beneficiaries.${index}.repIdIssuingCountry`)}
                                                placeholder="e.g. Mexico"
                                                $error={!!fieldErrors?.repIdIssuingCountry}
                                            />
                                            {fieldErrors?.repIdIssuingCountry && <ErrorText>{fieldErrors.repIdIssuingCountry.message}</ErrorText>}
                                        </FormGroup>
                                    )}
                                </FormRow>

                                <FormGroup>
                                    <Label>Home / Personal Address</Label>
                                    <StyledInput 
                                        type="text" 
                                        {...register(`beneficiaries.${index}.repAddress`)} 
                                        placeholder="Street Address" 
                                        $error={!!fieldErrors?.repAddress} 
                                    />
                                    {fieldErrors?.repAddress && <ErrorText>{fieldErrors.repAddress.message}</ErrorText>}
                                </FormGroup>

                                <FormRow>
                                    <FormGroup>
                                        <Label>City</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`beneficiaries.${index}.repCity`)} 
                                            placeholder="City" 
                                            $error={!!fieldErrors?.repCity} 
                                        />
                                        {fieldErrors?.repCity && <ErrorText>{fieldErrors.repCity.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>State / Province</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`beneficiaries.${index}.repState`)} 
                                            placeholder="State" 
                                            $error={!!fieldErrors?.repState} 
                                        />
                                        {fieldErrors?.repState && <ErrorText>{fieldErrors.repState.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>ZIP / Postal Code</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`beneficiaries.${index}.repZipCode`)} 
                                            placeholder="ZIP Code" 
                                            $error={!!fieldErrors?.repZipCode} 
                                        />
                                        {fieldErrors?.repZipCode && <ErrorText>{fieldErrors.repZipCode.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormGroup>
                                    <Label>Country of Residence</Label>
                                    <StyledInput 
                                        type="text" 
                                        {...register(`beneficiaries.${index}.repCountry`)} 
                                        placeholder="e.g. United States" 
                                        $error={!!fieldErrors?.repCountry} 
                                    />
                                    {fieldErrors?.repCountry && <ErrorText>{fieldErrors.repCountry.message}</ErrorText>}
                                </FormGroup>

                                <CheckboxLabel>
                                    <input type="checkbox" {...register(`beneficiaries.${index}.isPEP`)} />
                                    <span>This person is a Politically Exposed Person (PEP) or is affiliated with one.</span>
                                </CheckboxLabel>

                                {isPEP && (
                                    <FormGroup style={{ marginTop: '16px' }}>
                                        <Label>PEP Description</Label>
                                        <StyledInput
                                            as="textarea"
                                            rows="3"
                                            {...register(`beneficiaries.${index}.pepDetails`)}
                                            placeholder="Describe the political position or affiliation..."
                                            style={{ resize: 'vertical', minHeight: '80px' }}
                                            $error={!!fieldErrors?.pepDetails}
                                        />
                                        {fieldErrors?.pepDetails && <ErrorText>{fieldErrors.pepDetails.message}</ErrorText>}
                                    </FormGroup>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {fields.length < 4 && (
                <SecondaryButton 
                    type="button" 
                    onClick={() => {
                        append({
                            roleType: '',
                            repName: '',
                            repTitle: '',
                            ownershipPercentage: '',
                            repDob: '',
                            repIdType: '',
                            repSSN: '',
                            repIdIssuingCountry: '',
                            repEmail: '',
                            repPhoneDialCode: '+1',
                            repPhone: '',
                            repAddress: '',
                            repCity: '',
                            repState: '',
                            repZipCode: '',
                            repCountry: '',
                            isPEP: false,
                            pepDetails: ''
                        });
                        setExpandedIndex(fields.length); // Expand the newly added one
                    }}
                    style={{ width: '100%', justifyContent: 'center', marginBottom: '24px', marginTop: '8px' }}
                >
                    <Plus size={18} /> Add Another Beneficial Owner
                </SecondaryButton>
            )}

            {errors?.beneficiaries?.root && (
                <ErrorText style={{ textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>
                    {errors.beneficiaries.root.message}
                </ErrorText>
            )}

            <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                marginTop: '16px',
                padding: '14px 16px',
                borderRadius: '10px',
                backgroundColor: 'rgba(99, 102, 241, 0.06)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
            }}>
                <AlertCircle size={16} style={{ color: '#6366f1', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: 'inherit', opacity: 0.8 }}>
                    All beneficial owners are subject to OFAC and sanctions screening. By submitting, you authorize NYBANQ to verify identity through its compliance partners.
                </p>
            </div>
        </div>
    );
};

export default Step3BeneficialOwner;
